import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';

import { hasUnsavedChangesInRegistry } from '../../lib/unsavedChanges/unsavedChangesRegistry';

import type { UnsavedChangesContextValue } from './unsavedChangesContextTypes';
import { useUnsavedChangesContextValue } from './useUnsavedChangesContextValue';

export type {
  RegisterUnsavedChangesOptions,
  UnsavedChangesRegistration,
} from './unsavedChangesContextTypes';

export const UnsavedChangesContext = createContext<UnsavedChangesContextValue | undefined>(
  undefined,
);

interface UnsavedChangesProviderProps {
  children: ReactNode;
}

export function UnsavedChangesProvider({ children }: UnsavedChangesProviderProps): ReactElement {
  const contextValue = useUnsavedChangesContextValue();
  const handledBlockedLocationRef = useRef<string | null>(null);
  const { requestLeave } = contextValue;

  const blocker = useBlocker(
    useCallback(({ currentLocation, nextLocation }) => {
      if (!hasUnsavedChangesInRegistry()) {
        return false;
      }

      return (
        currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash
      );
    }, []),
  );

  useBeforeUnload(
    useCallback((event) => {
      if (!hasUnsavedChangesInRegistry()) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    }, []),
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      handledBlockedLocationRef.current = null;
      return;
    }

    // Registry may have been cleared after the block (e.g. redirect to login); do not stall navigation.
    if (!hasUnsavedChangesInRegistry()) {
      blocker.proceed();
      return;
    }

    const blockedLocationKey = blocker.location
      ? `${blocker.location.pathname}${blocker.location.search}${blocker.location.hash}`
      : 'blocked';

    if (handledBlockedLocationRef.current === blockedLocationKey) {
      return;
    }

    handledBlockedLocationRef.current = blockedLocationKey;

    let isCancelled = false;

    void (async () => {
      const confirmed = await requestLeave();

      if (isCancelled) {
        return;
      }

      if (confirmed) {
        blocker.proceed();
        return;
      }

      blocker.reset();
    })();

    return () => {
      isCancelled = true;
    };
  }, [blocker, blocker.location, blocker.state, requestLeave]);

  const memoizedValue = useMemo(() => contextValue, [contextValue]);

  return (
    <UnsavedChangesContext.Provider value={memoizedValue}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges(): UnsavedChangesContextValue {
  const context = useContext(UnsavedChangesContext);

  if (!context) {
    throw new Error(
      'useUnsavedChanges must be used within UnsavedChangesProvider or UnsavedChangesModalProvider',
    );
  }

  return context;
}
